import type {
  AnalyzedRule,
  CallChannelState,
  CapabilitiesState,
  ChatChannelState,
  ComplianceState,
  EmailChannelState,
  EmergencyState,
  KbEntry,
  KnowledgeBaseState,
  LockedProtection,
  PersonalityState,
  PracticeRulesState,
  SmsChannelState,
  VoicePreset,
} from './types';

export const RCPT_SETTINGS_VOICE_PRESETS: VoicePreset[] = [
  { id: 'ara', name: 'Ara', type: 'Female', tone: 'Warm, friendly', blurb: 'Balanced and conversational. Default for most US dental practices.' },
  { id: 'eve', name: 'Eve', type: 'Female', tone: 'Energetic, upbeat', blurb: 'Engaging and enthusiastic. Suits welcoming, high-energy practices.' },
  { id: 'rex', name: 'Rex', type: 'Male', tone: 'Confident, clear', blurb: 'Professional and articulate. Good fit for cosmetic and specialty practices.' },
  { id: 'sal', name: 'Sal', type: 'Neutral', tone: 'Smooth, balanced', blurb: 'Versatile across contexts. Useful when you want a neutral, calming presence.' },
  { id: 'leo', name: 'Leo', type: 'Male', tone: 'Authoritative, strong', blurb: 'Decisive and commanding. Works well for established, traditional offices.' },
];

export const RCPT_SETTINGS_INITIAL_PERSONALITY: PersonalityState = {
  name: 'Elva',
  voice: 'ara',
  warmth: 68,
  formality: 42,
  greetingBusiness: 'Hi, this is Elva at Bayside Dental. How can I help you today?',
  greetingAfterHours:
    "Hi, this is Elva at Bayside Dental. We're currently closed, but I can help you book an appointment or leave a message for the team.",
  multilingual: true,
};

export const RCPT_SETTINGS_INITIAL_PRACTICE_RULES: PracticeRulesState = {
  rules: `Never give medical advice. Always offer a clinician callback for clinical questions.

For new patients, always verify insurance before booking.

Don't book Dr. Lin past 4pm — she has evening commitments.

When a patient seems anxious, slow down and offer reassurance.

Never quote specific dollar amounts unless we've verified insurance.

Refuse to schedule on Sundays — we're closed.

Don't accept patients who haven't paid outstanding balances.`,
  penaltyWindow: '24',
  enforcementTone: 'soft',
  saveAggressiveness: 2,
  lastAnalyzed: 'May 14, 2026 9:42 AM',
};

export const RCPT_SETTINGS_ANALYZED_RULES: AnalyzedRule[] = [
  {
    status: 'enabled',
    quote: 'Never give medical advice. Always offer a clinician callback for clinical questions.',
    explanation:
      "Maps directly to a hard boundary in Elva's instructions. She'll decline clinical questions and offer a same-day visit or clinician callback instead.",
  },
  {
    status: 'enabled',
    quote: 'When a patient seems anxious, slow down and offer reassurance.',
    explanation:
      "Maps to Elva's anxiety-detection routine. She lowers her pace, adds reassurance language, and offers an extra information step.",
  },
  {
    status: 'enabled',
    quote: "Never quote specific dollar amounts unless we've verified insurance.",
    explanation:
      'Becomes a guard rail on the pricing module. Elva quotes ranges or defers to staff until eligibility comes back from the API.',
  },
  {
    status: 'needs-config',
    quote: "Don't book Dr. Lin past 4pm — she has evening commitments.",
    explanation:
      'Provider availability windows are configured in Practice Management → Schedules. This rule mirrors that policy — confirm the 4pm cutoff is also set there for full safety.',
  },
  {
    status: 'enabled',
    quote: "Refuse to schedule on Sundays — we're closed.",
    explanation:
      'Sunday closure is also configured in Practice Management → Business hours. This rule provides an extra safeguard.',
  },
  {
    status: 'rejected',
    quote: "Don't accept patients who haven't paid outstanding balances.",
    explanation:
      "Pre-screening for unpaid balances requires CRM integration that isn't currently configured. Elva would need read access to your AR aging report to apply this rule. Set up the CRM connector in Tool use, or ask patients to settle balances at the office instead.",
    action: "This rule won't be applied. Edit your text or fix the prerequisite to retry.",
  },
];

export const RCPT_SETTINGS_INITIAL_EMERGENCY: EmergencyState = {
  definition:
    'Severe pain rated 7 or higher out of 10. Visible facial swelling. Trauma to teeth or jaw from an accident. Uncontrolled bleeding more than 12 hours after an extraction. Any post-op complication within 72 hours of a surgical procedure. Active dental abscess with fever.',
  allowDoubleBooking: true,
  reservedSlotsDaily: 2,
  maxEmergencyBookingsDaily: 4,
  skipInsuranceVerification: true,
  afterHoursRoute: 'oncall',
  lighterVerification: true,
};

export const RCPT_SETTINGS_EMERGENCY_RULES: AnalyzedRule[] = [
  {
    status: 'enabled',
    quote: 'Severe pain rated 7 or higher out of 10',
    explanation: "Maps to Elva's pain-triage threshold. She'll ask for a pain rating and bypass standard scheduling if 7+.",
  },
  {
    status: 'enabled',
    quote: 'Visible facial swelling',
    explanation: 'Becomes a same-day triage trigger. Elva asks for a description and flags it for staff.',
  },
  {
    status: 'enabled',
    quote: 'Trauma to teeth or jaw from an accident',
    explanation: 'Same-day emergency. Skips standard intake and routes directly to the on-call dentist.',
  },
  {
    status: 'enabled',
    quote: 'Uncontrolled bleeding more than 12 hours after an extraction',
    explanation: 'Post-op alert. Elva collects details and pages the on-call provider.',
  },
  {
    status: 'needs-config',
    quote: 'Active dental abscess with fever',
    explanation:
      "Pairs two clinical signals. Elva will treat 'abscess' alone as emergency too — confirm if you want both signals required.",
  },
];

export const RCPT_SETTINGS_KB_CATEGORIES = [
  { id: 'info', label: 'Practice info' },
  { id: 'services', label: 'Services & procedures' },
  { id: 'fees', label: 'Fees & pricing' },
  { id: 'insurance', label: 'Insurance & payment' },
  { id: 'policy', label: 'Policies' },
  { id: 'providers', label: 'Providers & staff' },
  { id: 'forms', label: 'Forms' },
  { id: 'faq', label: 'Frequently asked' },
] as const;

const KB_ENTRIES: KbEntry[] = [
  { id: 1, title: 'Office hours', category: 'info', body: 'Monday–Friday 8 AM to 6 PM, Saturdays 9 AM to 1 PM. Closed Sundays and major holidays.', lastUpdated: 'Nov 3, 2026', active: true },
  { id: 2, title: 'Parking & directions', category: 'info', body: 'Free 2-hour parking in the building garage on Fillmore. Street parking on Pine and Bush. Patients can validate parking at the front desk.', lastUpdated: 'Oct 28, 2026', active: true },
  { id: 3, title: 'Phone & email contact', category: 'info', body: 'Main: (415) 555-0100. After-hours emergencies: (415) 555-0911. Non-urgent: hello@baysidedental.com. We respond to email within 1 business day.', lastUpdated: 'Oct 18, 2026', active: true },
  { id: 4, title: 'What we offer', category: 'services', body: 'General preventive (cleanings, exams), restorative (fillings, crowns), cosmetic (whitening, veneers, Invisalign), endodontics (Tue/Thu), and implants.', lastUpdated: 'Oct 12, 2026', active: true },
  { id: 5, title: 'Invisalign at Bayside', category: 'services', body: 'Platinum+ Invisalign provider. 12–18 months for most adult cases. Free 15-minute consult with Dr. Lin.', lastUpdated: 'Sept 30, 2026', active: true },
  { id: 6, title: 'Adult cleaning fees', category: 'fees', body: 'Adult cleaning: $145 (typical with PPO insurance). Co-pay varies by plan. Patients without insurance get a 15% courtesy discount when paying at the visit.', lastUpdated: 'Nov 2, 2026', active: true },
  { id: 7, title: 'Crown fees', category: 'fees', body: 'PFM: $1,150–1,350. All-zirconia: $1,250–1,450. Gold: $1,450–1,650. Patient responsibility depends on plan.', lastUpdated: 'Nov 2, 2026', active: true },
  { id: 8, title: 'Insurance plans accepted', category: 'insurance', body: 'Delta Dental PPO, MetLife, Cigna, Aetna, most Blue Shield PPO plans. Out-of-network with Kaiser and most HMOs.', lastUpdated: 'Oct 1, 2026', active: true },
  { id: 9, title: 'Payment plans', category: 'insurance', body: 'In-house 3- and 6-month plans for treatment over $500. CareCredit accepted up to 24 months.', lastUpdated: 'Sept 18, 2026', active: true },
  { id: 10, title: 'Cancellation policy', category: 'policy', body: "We ask for 24 hours' notice for cancellations. Same-day cancellations or no-shows may be charged $75 after the second occurrence within 12 months.", lastUpdated: 'Aug 5, 2026', active: true },
  { id: 11, title: 'Late arrivals', category: 'policy', body: "Patients arriving more than 15 minutes late may need to reschedule depending on the day's flow.", lastUpdated: 'Aug 5, 2026', active: true },
  { id: 12, title: 'Dr. Yuki Lin', category: 'providers', body: 'Dr. Lin completed her DDS at UCSF and has been practicing since 2014. Specialties: cosmetic dentistry, Invisalign, and implants. In office Mon–Thu.', lastUpdated: 'Jul 12, 2026', active: true },
  { id: 13, title: 'Sarah — lead hygienist', category: 'providers', body: 'Sarah has been our lead hygienist since 2018. Specializes in perio maintenance and patient comfort.', lastUpdated: 'Jul 12, 2026', active: true },
  { id: 14, title: 'New patient intake forms', category: 'forms', body: 'Sent via secure SMS or email after booking. Includes medical history, HIPAA authorization, and financial agreement. ~8 minutes to complete.', lastUpdated: 'Oct 15, 2026', active: true },
  { id: 15, title: 'Are you accepting new patients?', category: 'faq', body: 'Yes, currently accepting new patients across all providers. Wait time currently 2–3 weeks.', lastUpdated: 'Nov 1, 2026', active: true },
];

export const RCPT_SETTINGS_INITIAL_KB: KnowledgeBaseState = {
  entries: KB_ENTRIES,
  search: '',
  activeCategory: 'all',
};

export const RCPT_SETTINGS_CAP_CHANNELS = [
  { id: 'call' as const, label: 'Call', short: 'Phone' },
  { id: 'sms' as const, label: 'SMS', short: 'SMS' },
  { id: 'email' as const, label: 'Email', short: 'Email' },
  { id: 'chat' as const, label: 'Chat', short: 'Chat' },
];

export const RCPT_SETTINGS_CAP_GROUPS = [
  {
    id: 'booking',
    num: '01',
    title: 'Scheduling and managing appointments',
    sub: 'Actions that put a patient on the schedule, take them off, or move them.',
    actions: [
      { id: 'bookReturning', label: 'Book a new appointment (returning patients)', note: "Existing patients only. Elva uses the patient's chart for context." },
      { id: 'bookNew', label: 'Book a new appointment (new patients)', note: 'Creates a new patient record and books their first visit.' },
      { id: 'reschedule', label: 'Reschedule an appointment', note: 'Move an existing appointment to a new time.' },
      { id: 'cancel', label: 'Cancel an appointment', note: 'Remove an appointment — subject to your cancellation policy.' },
      { id: 'confirm', label: 'Confirm appointments', note: 'Respond to confirmation outreach with yes/no/reschedule.' },
      { id: 'bookFamily', label: 'Book for the whole family', note: 'Schedule multiple family members in one conversation.' },
    ],
  },
  {
    id: 'records',
    num: '02',
    title: 'Adding and updating patient information',
    sub: 'Touching the patient record itself — new patients, contact info, insurance.',
    actions: [
      { id: 'addPatient', label: 'Add a new patient', note: 'Capture name, DOB, contact info, and insurance for a new record.' },
      { id: 'updateContactInfo', label: 'Update existing patient contact info', note: 'Change phone, email, or address on a verified patient record.' },
      { id: 'gatherInsurance', label: 'Gather insurance information', note: "Capture or update insurance details so RCM can verify before the visit." },
    ],
  },
  {
    id: 'info',
    num: '03',
    title: 'Sharing facts and answering questions',
    sub: 'What Elva can say or share when asked — pulls from Knowledge base.',
    actions: [
      { id: 'shareAvailability', label: 'Share provider availability', note: 'Tell the patient when their preferred provider next has openings.' },
      { id: 'providePricing', label: 'Provide pricing', note: 'Quote fees for procedures. Uses entries from Knowledge base.' },
      { id: 'shareInsuranceList', label: 'Share in-network insurance list', note: 'Tell callers which carriers you accept.' },
      { id: 'detectReason', label: 'Detect appointment reason', note: 'Ask what the visit is for so the right slot type is booked.' },
      { id: 'respondFAQ', label: 'Respond to FAQ questions', note: 'Answer any question that has a matching Knowledge base entry.' },
    ],
  },
];

export const RCPT_SETTINGS_CAP_ACTIONS_FLAT = RCPT_SETTINGS_CAP_GROUPS.flatMap((g) =>
  g.actions.map((a) => a.id),
);

export const RCPT_SETTINGS_INITIAL_CAPABILITIES: CapabilitiesState = {
  actions: {
    bookReturning: { call: true, sms: true, email: false, chat: true },
    bookNew: { call: true, sms: false, email: false, chat: true },
    reschedule: { call: true, sms: true, email: true, chat: true },
    cancel: { call: true, sms: true, email: true, chat: true },
    confirm: { call: true, sms: true, email: true, chat: true },
    bookFamily: { call: true, sms: false, email: false, chat: false },
    addPatient: { call: true, sms: false, email: false, chat: true },
    updateContactInfo: { call: true, sms: false, email: false, chat: true },
    gatherInsurance: { call: true, sms: false, email: false, chat: true },
    shareAvailability: { call: true, sms: true, email: true, chat: true },
    providePricing: { call: true, sms: true, email: true, chat: true },
    shareInsuranceList: { call: true, sms: true, email: true, chat: true },
    detectReason: { call: true, sms: true, email: false, chat: true },
    respondFAQ: { call: true, sms: true, email: true, chat: true },
  },
  quoteStyle: 'starting-at',
};

export const RCPT_SETTINGS_LOCKED_PROTECTIONS: LockedProtection[] = [
  { id: 'no-medical-advice', text: 'Never provide medical advice or diagnoses', note: "All clinical questions route to providers. Elva won't interpret symptoms, suggest treatment, or evaluate severity." },
  { id: 'no-medications', text: 'Never recommend medications or dosages', note: "Even general guidance like 'take ibuprofen for the pain' routes to a provider or the on-call number." },
  { id: 'no-coverage-promises', text: 'Never promise insurance coverage outcomes', note: "Elva says 'we'll verify with your plan' — never 'you're covered.'" },
  { id: '911-redirect', text: 'Always redirect life-threatening emergencies to 911', note: 'Chest pain, difficulty breathing, severe trauma, or stroke symptoms get an immediate 911 redirect.' },
  { id: 'crisis-referral', text: 'Refer self-harm or crisis signals to 988 and a live human', note: 'Elva pauses scheduling, provides 988 Suicide & Crisis Lifeline, and hands off to staff.' },
];

export const RCPT_SETTINGS_INITIAL_COMPLIANCE: ComplianceState = {
  verifyIdentityBeforeSharing: true,
  patientNameInVoicemails: false,
  shareWithThirdParties: false,
  quoteUnverifiedFees: false,
  discussBalancesOnPhone: false,
  recordAllCalls: true,
  twoPartyConsent: true,
  discloseAINature: true,
};

export const RCPT_SETTINGS_VERIFICATION_METHODS = [
  { id: 'phoneNumber' as const, label: 'Phone number', desc: 'Last 4 digits. Quick to verify and the most patient-friendly.' },
  { id: 'dob' as const, label: 'Date of birth', desc: "Month, day, and year. Highest confidence but slightly more friction." },
  { id: 'zip' as const, label: 'ZIP code', desc: "5-digit ZIP on the patient record. Useful as a fallback when DOB isn't recognized." },
  { id: 'fullName' as const, label: 'Full name', desc: 'Exact match including spelling. The lightest method — useful for low-risk channels like chat.' },
];

export const RCPT_SETTINGS_INITIAL_CALL: CallChannelState = {
  verification: { phoneNumber: true, dob: true, zip: false, fullName: false },
  warmTransfer: {
    rules: [
      { id: 1, topic: 'Billing disputes, payment plans, or financial questions', phoneNumber: '+1 (415) 555-0101' },
      { id: 2, topic: "Treatment plan questions for procedures we've quoted", phoneNumber: '+1 (415) 555-0102' },
      { id: 3, topic: 'Anything else where the patient asks for a human, or sentiment turns frustrated', phoneNumber: '+1 (415) 555-0100' },
    ],
  },
};

export const RCPT_SETTINGS_INITIAL_SMS: SmsChannelState = {
  verification: { phoneNumber: true, dob: false, zip: false, fullName: false },
  warmTransfer: {
    rules: [
      { id: 1, topic: 'Complaints about prior care or staff behavior' },
      { id: 2, topic: "Clinical questions that need a provider's judgment" },
      { id: 3, topic: 'Billing disputes or refund requests' },
    ],
  },
  signature: '— Bayside Dental',
  quietHoursEnabled: true,
  quietHoursStart: '21:00',
  quietHoursEnd: '08:00',
};

export const RCPT_SETTINGS_INITIAL_EMAIL: EmailChannelState = {
  verification: { phoneNumber: false, dob: false, zip: false, fullName: true },
  warmTransfer: {
    rules: [
      { id: 1, topic: 'Legal questions, demand letters, or anything mentioning litigation' },
      { id: 2, topic: 'Complaints about prior care or refund requests' },
      { id: 3, topic: 'Emails where the patient sounds emotional, urgent, or distressed' },
    ],
  },
  signature: 'Best,\nElva at Bayside Dental\n(415) 555-0100\nbaysidedental.com',
  responseSLAMinutes: '60',
  autoAcknowledge: true,
};

export const RCPT_SETTINGS_INITIAL_CHAT: ChatChannelState = {
  verification: { phoneNumber: false, dob: false, zip: false, fullName: true },
  warmTransfer: {
    rules: [
      { id: 1, topic: 'Patient explicitly asks to talk to a person' },
      { id: 2, topic: 'Complaints about prior care or refund requests' },
      { id: 3, topic: 'Conversation goes beyond 8 turns without a clear resolution' },
    ],
  },
  welcomeMessage: "Hi! I'm Elva, the AI receptionist at Bayside Dental. How can I help you today?",
  preChatForm: false,
  offlineBehavior: 'schedule-callback',
};
